import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  ShieldCheck, Plus, Trash2, Save, RefreshCw, AlertTriangle,
  CheckCircle2, XCircle, Sliders, Info, ChevronDown, ChevronUp
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
export type ToneStyle = 'Headless-CMS-Structure' | 'Marketing-Conversational';

export interface BrandGuardrails {
  toneStyle: ToneStyle;
  negativeConstraints: string[];   // phrases to strip / ban globally
  positiveEnforcements: string[];  // absolute criteria to enforce
  enabled: boolean;
}

const STORAGE_KEY = 'uxcf_brand_guardrails';

export function loadGuardrails(): BrandGuardrails {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as BrandGuardrails;
  } catch { /* ignore */ }
  return {
    toneStyle: 'Marketing-Conversational',
    negativeConstraints: [],
    positiveEnforcements: [],
    enabled: false,
  };
}

export function saveGuardrails(g: BrandGuardrails): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(g));
}

/** Returns the guardrails as a prepended system block for AI prompts */
export function buildGuardrailsBlock(g: BrandGuardrails): string {
  if (!g.enabled) return '';
  const lines: string[] = [
    '=== BRAND GUARDRAILS (global — prepended to all instructions) ===',
    `TONE: ${g.toneStyle}`,
  ];
  if (g.negativeConstraints.length > 0) {
    lines.push('BANNED PHRASES (never use, strip from output):');
    g.negativeConstraints.forEach(p => lines.push(`  - "${p}"`));
  }
  if (g.positiveEnforcements.length > 0) {
    lines.push('ENFORCED CRITERIA (must be present in output):');
    g.positiveEnforcements.forEach(p => lines.push(`  + "${p}"`));
  }
  lines.push('=== END GUARDRAILS ===');
  return lines.join('\n');
}

// ─── Tag chip input ───────────────────────────────────────────────────────────
const ChipInput: React.FC<{
  label: string;
  description: string;
  color: 'red' | 'green';
  chips: string[];
  onChange: (chips: string[]) => void;
  placeholder: string;
}> = ({ label, description, color, chips, onChange, placeholder }) => {
  const [input, setInput] = useState('');

  const add = () => {
    const v = input.trim();
    if (v && !chips.includes(v)) onChange([...chips, v]);
    setInput('');
  };

  const remove = (c: string) => onChange(chips.filter(x => x !== c));

  const colorMap = {
    red:   { chip: 'bg-rose-500/15 border-rose-500/30 text-rose-300', icon: <XCircle className="w-3.5 h-3.5 text-rose-400" />, ring: 'focus:border-rose-500/50' },
    green: { chip: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300', icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />, ring: 'focus:border-emerald-500/50' },
  };
  const c = colorMap[color];

  return (
    <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        {c.icon}
        <div>
          <p className="text-sm font-semibold text-white">{label}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {chips.map(chip => (
          <span
            key={chip}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border font-medium ${c.chip}`}
          >
            {chip}
            <button onClick={() => remove(chip)} className="hover:opacity-70 transition-opacity">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {chips.length === 0 && (
          <span className="text-[11px] text-slate-600 italic">No constraints added yet</span>
        )}
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <input
          className={`flex-1 bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 ${c.ring} focus:outline-none transition-colors`}
          placeholder={placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button
          onClick={add}
          disabled={!input.trim()}
          className="px-3 py-2 bg-[#1a1a2e] hover:bg-violet-600/40 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-slate-300 hover:text-white border border-white/[0.08] transition-all"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// X icon (not from lucide to avoid confusion)
const X: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const BrandGuardrailsPage: React.FC = () => {
  const [g, setG]           = useState<BrandGuardrails>(loadGuardrails);
  const [saved, setSaved]   = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => { saveGuardrails(g); }, [g]);

  const handleSave = () => {
    saveGuardrails(g);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const previewBlock = buildGuardrailsBlock(g);

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-[0_0_16px_rgba(139,92,246,0.4)]">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Brand Guardrails</h1>
              <p className="text-sm text-slate-400">Global constraints prepended to every AI generation node</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}
          >
            {saved ? <><CheckCircle2 className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save Guardrails</>}
          </button>
        </div>

        {/* Enable toggle */}
        <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${g.enabled ? 'text-amber-400' : 'text-slate-600'}`} />
            <div>
              <p className="font-semibold text-white text-sm">Enable Global Guardrails</p>
              <p className="text-[11px] text-slate-500 mt-0.5">When active, these constraints are injected at the top of every AI system prompt across all generation nodes.</p>
            </div>
          </div>
          <button
            onClick={() => setG(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`relative inline-flex items-center w-12 h-6 rounded-full transition-colors flex-shrink-0 ${g.enabled ? 'bg-violet-600' : 'bg-slate-700'}`}
          >
            <span className={`absolute w-5 h-5 rounded-full bg-white shadow transition-transform ${g.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Tone style */}
        <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-violet-400" />
            <p className="font-semibold text-white text-sm">Tone Style</p>
          </div>
          <p className="text-[11px] text-slate-500">Sets the baseline voice applied to all generated content.</p>
          <div className="grid grid-cols-2 gap-3">
            {(['Headless-CMS-Structure', 'Marketing-Conversational'] as ToneStyle[]).map(tone => (
              <button
                key={tone}
                onClick={() => setG(prev => ({ ...prev, toneStyle: tone }))}
                className={`rounded-xl border p-4 text-left transition-all ${g.toneStyle === tone ? 'border-violet-500/40 bg-violet-500/10 shadow-[0_0_10px_rgba(139,92,246,0.08)]' : 'border-white/[0.06] bg-[#0f0f1a] hover:border-violet-500/25'}`}
              >
                <p className={`font-semibold text-sm ${g.toneStyle === tone ? 'text-violet-300' : 'text-white'}`}>
                  {tone === 'Headless-CMS-Structure' ? 'Headless CMS Structure' : 'Marketing Conversational'}
                </p>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  {tone === 'Headless-CMS-Structure'
                    ? 'Framework-agnostic Structured JSON payloads. Outputs isolated fields for markdown content, media references, and SEO metadata. Optimised for Strapi, Sanity, and Contentful.'
                    : 'Outcome-focused, approachable, peer-to-peer. Strong hooks, human language, CTA-driven.'}
                </p>
                {g.toneStyle === tone && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-violet-400">
                    <CheckCircle2 className="w-3 h-3" /> Active
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Negative constraints */}
        <ChipInput
          label="Negative Constraints (Banned Phrases)"
          description="These phrases will be stripped or avoided globally across all AI-generated content."
          color="red"
          chips={g.negativeConstraints}
          onChange={neg => setG(prev => ({ ...prev, negativeConstraints: neg }))}
          placeholder={`e.g. "Maida", "Refined Flour", "unlock the potential"`}
        />

        {/* Positive enforcements */}
        <ChipInput
          label="Positive Enforcements (Absolute Criteria)"
          description="These criteria must be present in every generated output. AI will be instructed to include them."
          color="green"
          chips={g.positiveEnforcements}
          onChange={pos => setG(prev => ({ ...prev, positiveEnforcements: pos }))}
          placeholder={`e.g. "100% Whole Wheat", "Task-oriented DITA XML"`}
        />

        {/* Preview system block */}
        <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] overflow-hidden">
          <button
            onClick={() => setPreview(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-white">Preview Injected System Block</span>
              {!g.enabled && <span className="text-[10px] text-slate-600 bg-slate-700/50 border border-slate-600/40 rounded-full px-2 py-0.5">Disabled</span>}
            </div>
            {preview ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </button>
          {preview && (
            <div className="border-t border-white/[0.05] p-4">
              <pre className="text-[11px] text-slate-300 font-mono whitespace-pre-wrap leading-relaxed bg-[#0a0a14] rounded-lg p-3 border border-white/[0.06]">
                {previewBlock || '(Guardrails disabled — no block will be injected)'}
              </pre>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Banned Phrases', value: g.negativeConstraints.length, color: 'text-rose-400' },
            { label: 'Enforced Criteria', value: g.positiveEnforcements.length, color: 'text-emerald-400' },
            { label: 'Status', value: g.enabled ? 'Active' : 'Off', color: g.enabled ? 'text-violet-400' : 'text-slate-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-3 text-center">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Reset */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              const def: BrandGuardrails = { toneStyle: 'Marketing-Conversational', negativeConstraints: [], positiveEnforcements: [], enabled: false };
              setG(def);
              saveGuardrails(def);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#12121a]/80 hover:bg-white/[0.05] rounded-xl text-sm text-slate-400 hover:text-white border border-white/[0.07] transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset to defaults
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default BrandGuardrailsPage;
